/**
 * Transaction Sync Buttons Component
 *
 * Provides Sync and Full Sync buttons for triggering Plaid
 * transaction synchronization.
 *
 * @fileoverview Sync button controls for transaction list
 */

import React, { useState, useCallback } from 'react';
import {
  HStack,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MdSync, MdCloudSync } from 'react-icons/md';
import { useTransactionSync } from '../../hooks/useTransactionSync';
import { TransactionSyncResult } from '../../types/transactions';

/**
 * Props for TransactionSyncButtons
 */
interface TransactionSyncButtonsProps {
  /** Plaid token status - buttons disabled if not ACTIVE */
  plaidTokenStatus?: string | null;
  /** Callback after successful sync */
  onSyncComplete?: (result: TransactionSyncResult) => void;
  /** Whether component is disabled */
  isDisabled?: boolean;
  /** Optional: sync only this specific bank. If not provided, syncs all banks. */
  tokenId?: string;
  /** Optional: selected account IDs for scoped sync. Empty/undefined means all accounts. */
  accountIds?: string[];
  /** Lifted sync state from parent (optional — if provided, uses parent hook instead of internal) */
  syncIncremental?: (tokenId?: string, accountIds?: string[]) => Promise<TransactionSyncResult | null>;
  syncFull?: (tokenId?: string, accountIds?: string[]) => Promise<TransactionSyncResult | null>;
  isSyncing?: boolean;
  syncType?: 'incremental' | 'full' | 'reset' | null;
  syncError?: string | null;
}

/**
 * Transaction Sync Buttons
 *
 * Renders two buttons:
 * - Sync: Incremental sync from cursor position
 * - Full Sync: Reset cursor and fetch all transactions (with confirmation)
 *
 * If tokenId is provided, syncs only that bank. Otherwise syncs all banks.
 */
const TransactionSyncButtons: React.FC<TransactionSyncButtonsProps> = ({
  plaidTokenStatus,
  onSyncComplete,
  isDisabled = false,
  tokenId,
  accountIds,
  syncIncremental: syncIncrementalProp,
  syncFull: syncFullProp,
  isSyncing: isSyncingProp,
  syncType: syncTypeProp,
  syncError: syncErrorProp,
}) => {
  const toast = useToast();
  const { isOpen: isFullSyncOpen, onOpen: onFullSyncOpen, onClose: onFullSyncClose } = useDisclosure();
  const [isConfirming, setIsConfirming] = useState(false);

  // Use parent-lifted hook if provided, otherwise use internal instance
  const internal = useTransactionSync();
  const syncIncremental = syncIncrementalProp ?? internal.syncIncremental;
  const syncFull = syncFullProp ?? internal.syncFull;
  const isSyncing = isSyncingProp ?? internal.isSyncing;
  const syncType = syncTypeProp ?? internal.syncType;
  const error = syncErrorProp ?? internal.error;

  // Buttons disabled when no active Plaid connection or during sync
  const isButtonDisabled = isDisabled || isSyncing || plaidTokenStatus !== 'ACTIVE';

  /**
   * Format sync result for toast message
   */
  const formatSyncResult = (result: TransactionSyncResult): string => {
    const parts: string[] = [];
    if (result.added > 0) parts.push(`+${result.added} added`);
    if (result.modified > 0) parts.push(`~${result.modified} modified`);
    if (result.removed > 0) parts.push(`-${result.removed} removed`);

    if (parts.length === 0) {
      return 'No new transactions';
    }
    return parts.join(', ');
  };

  /**
   * Handle incremental sync button click
   */
  const handleSync = useCallback(async () => {
    const result = await syncIncremental(tokenId, accountIds);

    if (result?.success) {
      toast({
        title: 'Sync Complete',
        description: formatSyncResult(result),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onSyncComplete?.(result);
    } else {
      // Prefer the reason carried on the result: `error` is hook state read
      // from the closure captured before the await, so it still holds its
      // pre-sync value here. Queued syncs resolve their failures (job
      // failed, job not found, poll timeout) as { success: false, error }
      // rather than throwing, so relying on `error` alone left those silent.
      toast({
        title: 'Sync Failed',
        description:
          result?.error || error || 'An error occurred during sync',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [syncIncremental, tokenId, accountIds, error, toast, onSyncComplete]);

  /**
   * Handle full sync confirmation
   */
  const handleFullSyncConfirm = useCallback(async () => {
    setIsConfirming(true);
    onFullSyncClose();

    const result = await syncFull(tokenId, accountIds);

    setIsConfirming(false);

    if (result?.success) {
      toast({
        title: 'Full Sync Complete',
        description: formatSyncResult(result),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onSyncComplete?.(result);
    } else {
      // See handleSync above re: preferring result.error over the stale
      // `error` closure.
      toast({
        title: 'Full Sync Failed',
        description:
          result?.error || error || 'An error occurred during full sync',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [syncFull, tokenId, accountIds, error, toast, onFullSyncClose, onSyncComplete]);

  return (
    <>
      <HStack spacing={2}>
        {/* Incremental Sync Button */}
        <Button
          leftIcon={<MdSync />}
          size="sm"
          variant="outline"
          colorScheme="cyan"
          color="accent.primary"
          borderColor="xacto.electricCyan"
          onClick={handleSync}
          isLoading={isSyncing && syncType === 'incremental'}
          loadingText="Syncing"
          isDisabled={isButtonDisabled}
          _hover={{ bg: 'xacto.mutedNavy' }}
          _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
        >
          Sync
        </Button>

        {/* Full Sync Button */}
        <Button
          leftIcon={<MdCloudSync />}
          size="sm"
          variant="outline"
          colorScheme="orange"
          color="orange.300"
          borderColor="orange.300"
          onClick={onFullSyncOpen}
          isLoading={(isSyncing && syncType === 'full') || isConfirming}
          loadingText="Full Sync"
          isDisabled={isButtonDisabled}
          _hover={{ bg: 'xacto.mutedNavy' }}
          _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
        >
          Full Sync
        </Button>
      </HStack>

      {/* Full Sync Confirmation Modal */}
      <Modal isOpen={isFullSyncOpen} onClose={onFullSyncClose} isCentered>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="bg.surface" borderColor="xacto.borderGray" borderWidth="1px">
          <ModalHeader color="text.primary">Confirm Full Sync</ModalHeader>
          <ModalCloseButton color="text.secondary" />
          <ModalBody>
            <VStack spacing={4} align="start">
              <Text color="text.secondary">
                This will reset the sync cursor and retrieve all transactions from Plaid
                (up to 2 years of data).
              </Text>
              <Text color="orange.300" fontSize="sm">
                This operation may take several minutes depending on transaction volume.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              color="text.secondary"
              mr={3}
              onClick={onFullSyncClose}
              _hover={{ bg: 'xacto.mutedNavy' }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleFullSyncConfirm}
              isLoading={isConfirming}
            >
              Confirm Full Sync
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TransactionSyncButtons;
